import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { SeLayoutsStore } from "./layouts/se.layouts.store";
import { SeOrgSecurityStore } from "./se.org.security.store";
import { Layouts } from "./layouts/se.layouts";
import { AlertService } from "../../services/alert.service";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { TelemetryService } from "../../services/telemetry.service";

@TypeManagerDecorator("s25-ng-se-org-event-layout-banner")
@Component({
    selector: "s25-ng-se-org-event-layout-banner",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25ButtonComponent],
    template: `
        @if (customLayout()) {
            <div class="event-layout-banner" role="status" data-id="org-event-layout-banner">
                <p>This organization uses a custom Event Details layout.</p>
                @if (canReset()) {
                    <s25-ng-button
                        [type]="'outline'"
                        [onClick]="resetLayout"
                        [isLoading]="resetting()"
                        [buttonClass]="'event-layout-reset'"
                    >
                        Reset to default
                    </s25-ng-button>
                }
            </div>
        }
    `,
    styles: `
        .event-layout-banner {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 0.75rem 1rem;
            background: var(--color--background-info);
            border: 1px solid var(--color--border-info);
            border-radius: 8px;
            margin-bottom: 1.5rem;
        }
        .event-layout-banner p {
            margin: 0;
        }
        :host ::ng-deep .event-layout-reset {
            white-space: nowrap;
        }
    `,
})
export class SeOrgEventLayoutBannerComponent {
    readonly orgId = input.required<number>();
    readonly editable = input(false);

    private readonly layoutsStore = inject(SeLayoutsStore);
    private readonly orgSecStore = inject(SeOrgSecurityStore);

    readonly resetting = signal(false);

    readonly customLayout = computed(() => this.layoutsStore.getOrgCustomLayoutForPage(this.orgId(), "event-details"));

    readonly canReset = computed(
        () => this.editable() && this.orgSecStore.canManageLayouts() && !!this.customLayout()?.id,
    );

    readonly resetLayout = async () => {
        const layout = this.customLayout();
        if (!layout?.id || !this.canReset() || this.resetting()) return;
        const confirmed = await AlertService.confirm(Layouts.resetToDefaultConfirmMessage());
        if (!confirmed) return;
        this.resetting.set(true);
        try {
            await this.layoutsStore.deleteLayout(layout.id);
            TelemetryService.sendWithSub("StudentEngagement", "Layouts", "EventDetailsReset");
            await AlertService.alert("Layout reset to the global default.");
        } catch (caughtError) {
            const message = caughtError instanceof Error ? caughtError.message : String(caughtError);
            await AlertService.alert(`Reset failed: ${message}`);
        } finally {
            this.resetting.set(false);
        }
    };
}
